"use client";

import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "../../components/account/Navbar";
import SocialSidebar from "../../components/social/layout/SocialSidebar";
import { useAuth } from "../../hooks/useAuth";
import { clinicAPI, locationAPI } from "../../services/api";
import { getUserPlaceholderImage, resolveImageUrl, withPlaceholder } from "../../utils/imageUtils";
import { getUserTypeColor } from "../../utils/userTypeUtils";
import "../social/SocialStyling.css";
import "./ClinicListPage.css";

const PAGE_SIZE = 24;

const ClinicDirectory = () => {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const { user, getUserType } = useAuth();
    // Page chrome follows the logged-in user's user-type color (like the rest of the app)
    const userTypeColor = getUserTypeColor(getUserType?.());

    // Vets/moderators default to "my clinics" but can switch to browsing all
    // verified clinics; everyone else always browses.
    const userType = (getUserType?.() || user?.user_type || "").toUpperCase();
    const isStaffView = userType === "VETERINARIAN" || userType === "CLINIC_MODERATOR";
    const [scope, setScope] = useState(isStaffView ? "mine" : "all"); // mine | all
    const browsingAll = scope === "all";

    const localized = (en, ar) => (i18n.language?.startsWith("ar") ? (ar || en) : (en || ar));

    const [clinics, setClinics] = useState([]);
    const [pagination, setPagination] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [countryId, setCountryId] = useState("");
    const [cityId, setCityId] = useState("");
    const [countries, setCountries] = useState([]);
    const seqRef = useRef(0);

    useEffect(() => {
        const handler = setTimeout(() => setDebouncedSearch(search.trim()), 350);
        return () => clearTimeout(handler);
    }, [search]);

    // Location filter options (browse mode)
    useEffect(() => {
        if (!browsingAll) return;
        locationAPI.getCountries()
            .then((resp) => setCountries(resp?.data?.data || []))
            .catch(() => setCountries([]));
    }, [browsingAll]);

    const cities = useMemo(() => {
        const country = countries.find((c) => String(c.id) === String(countryId));
        return country?.cities || [];
    }, [countries, countryId]);

    const fetchClinics = useCallback(async (page = 1) => {
        const seq = ++seqRef.current;
        try {
            const params = !browsingAll
                ? { mine: 1, search: debouncedSearch || undefined, per_page: 60, page: 1 }
                : {
                    status: "verified",
                    search: debouncedSearch || undefined,
                    country_id: countryId || undefined,
                    city_id: cityId || undefined,
                    per_page: PAGE_SIZE,
                    page,
                };
            const resp = await clinicAPI.getClinics(params);
            if (seq !== seqRef.current) return;
            const payload = resp?.data?.data || {};
            let list = payload.clinics || [];
            // "My Clinics" must only ever contain clinics the user owns or
            // belongs to — enforce it client-side as well (guards against a
            // backend that does not understand the mine filter yet).
            if (!browsingAll) {
                list = list.filter((c) => c?.clinic?.is_owner || c?.clinic?.is_member);
            }
            const pg = payload.pagination || null;
            setClinics((prev) => (page === 1 ? list : [...prev, ...list]));
            setPagination(pg);
        } catch {
            if (seq === seqRef.current) setClinics((prev) => (page === 1 ? [] : prev));
        } finally {
            if (seq === seqRef.current) {
                setLoading(false);
                setLoadingMore(false);
            }
        }
    }, [browsingAll, debouncedSearch, countryId, cityId]);

    useEffect(() => {
        setLoading(true);
        fetchClinics(1);
    }, [fetchClinics]);

    const loadMore = () => {
        if (!pagination || pagination.current_page >= pagination.last_page || loadingMore) return;
        setLoadingMore(true);
        fetchClinics(pagination.current_page + 1);
    };

    return (
        <div className="clinic-directory" style={{ '--user-type-color': userTypeColor }}>
            <div className="clinic-directory-header">
                <div className="clinic-directory-header-row">
                    <h1 className="clinic-directory-title">
                        {!browsingAll
                            ? t("clinicPage.directory.mineTitle", { defaultValue: "My Clinics" })
                            : t("clinicPage.directory.browseTitle", { defaultValue: "Browse Clinics" })}
                    </h1>
                    {isStaffView && (
                        <div className="clinic-directory-scope-toggle">
                            <button
                                type="button"
                                className={`clinic-directory-scope-btn ${!browsingAll ? "active" : ""}`}
                                onClick={() => setScope("mine")}
                            >
                                {t("clinicPage.directory.scopeMine", { defaultValue: "My Clinics" })}
                            </button>
                            <button
                                type="button"
                                className={`clinic-directory-scope-btn ${browsingAll ? "active" : ""}`}
                                onClick={() => setScope("all")}
                            >
                                {t("clinicPage.directory.scopeAll", { defaultValue: "All Clinics" })}
                            </button>
                        </div>
                    )}
                </div>

                <div className="clinic-directory-filters">
                    <input
                        type="text"
                        className="clinic-directory-search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={t("clinicPage.directory.searchPlaceholder", { defaultValue: "Search clinics..." })}
                    />
                    {browsingAll && (
                        <>
                            <select
                                className="clinic-directory-select"
                                value={countryId}
                                onChange={(e) => {
                                    setCountryId(e.target.value);
                                    setCityId("");
                                }}
                            >
                                <option value="">
                                    {t("clinicPage.directory.allCountries", { defaultValue: "All Countries" })}
                                </option>
                                {countries.map((c) => (
                                    <option key={c.id} value={c.id}>{localized(c.name_en, c.name_ar)}</option>
                                ))}
                            </select>
                            <select
                                className="clinic-directory-select"
                                value={cityId}
                                onChange={(e) => setCityId(e.target.value)}
                                disabled={!countryId}
                            >
                                <option value="">
                                    {t("clinicPage.directory.allCities", { defaultValue: "All Cities" })}
                                </option>
                                {cities.map((c) => (
                                    <option key={c.id} value={c.id}>{localized(c.name_en, c.name_ar)}</option>
                                ))}
                            </select>
                        </>
                    )}
                </div>
            </div>

            {loading ? (
                <div className="clinic-directory-empty">{t("common.loading", { defaultValue: "Loading..." })}</div>
            ) : clinics.length === 0 ? (
                <div className="clinic-directory-empty">
                    {!browsingAll
                        ? t("clinicPage.directory.emptyMine", { defaultValue: "You are not part of any clinics yet." })
                        : t("clinicPage.directory.emptyBrowse", { defaultValue: "No clinics found for these filters." })}
                </div>
            ) : (
                <div className="clinic-directory-list">
                    {clinics.map((clinic) => {
                        const rating = parseFloat(clinic.average_rating || 0);
                        const isOwner = !!clinic?.clinic?.is_owner;
                        const isMember = !!clinic?.clinic?.is_member;
                        return (
                            <div
                                key={clinic.id}
                                className="clinic-directory-card"
                                onClick={() => navigate(`/clinic/${clinic.id}`)}
                            >
                                <img
                                    src={withPlaceholder(resolveImageUrl(clinic.logo), getUserPlaceholderImage())}
                                    alt={clinic.name_en}
                                    className="clinic-directory-logo"
                                />
                                <div className="clinic-directory-card-body">
                                    <span className="clinic-directory-name">
                                        {localized(clinic.name_en, clinic.name_ar)}
                                        {clinic.status === 'verified' && (
                                            <i className="fas fa-check-circle" style={{ color: '#00D4B3', marginInlineStart: '6px', fontSize: '14px' }}></i>
                                        )}
                                    </span>
                                    <span className="clinic-directory-meta">
                                        {clinic.clinic_type
                                            ? localized(clinic.clinic_type.name_en, clinic.clinic_type.name_ar)
                                            : ""}
                                        {clinic.city?.name_en
                                            ? ` · ${localized(clinic.city.name_en, clinic.city.name_ar)}${clinic.country?.name_en ? `, ${localized(clinic.country.name_en, clinic.country.name_ar)}` : ''}`
                                            : ""}
                                    </span>
                                    <span className="clinic-directory-rating">
                                        <span className="clinic-stars">
                                            {[1, 2, 3, 4, 5].map((star) => (
                                                <i
                                                    key={star}
                                                    className={`${rating >= star - 0.5 ? "fas" : "far"} fa-star`}
                                                    style={{ color: rating >= star - 0.5 ? "#F59E0B" : "#D1D5DB" }}
                                                ></i>
                                            ))}
                                        </span>
                                        {rating > 0
                                            ? <span className="clinic-directory-rating-text">{rating.toFixed(1)} ({clinic.ratings_count || 0})</span>
                                            : <span className="clinic-directory-rating-text muted">{t('clinicPage.noRatings', { defaultValue: 'No reviews yet' })}</span>}
                                    </span>
                                </div>
                                <div className="clinic-directory-card-actions" onClick={(e) => e.stopPropagation()}>
                                    {isStaffView && (isOwner || isMember) && (
                                        <span className={`clinic-directory-badge ${isOwner ? "owner" : ""}`}>
                                            {isOwner
                                                ? t("clinicPage.roleOwner", { defaultValue: "Owner" })
                                                : t("clinicPage.roleMember", { defaultValue: "Team Member" })}
                                        </span>
                                    )}
                                    {!isStaffView && userType === 'PET_GUARDIAN' && (
                                        <button
                                            type="button"
                                            className="clinic-directory-btn clinic-directory-btn-primary"
                                            onClick={() => navigate(`/medical/dashboard/petguardian/appointment-request?clinicId=${clinic.id}`)}
                                        >
                                            <i className="fas fa-calendar-check"></i>
                                            {t('clinicPage.bookAppointment', { defaultValue: 'Book Appointment' })}
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        className="clinic-directory-btn clinic-directory-btn-secondary"
                                        onClick={() => navigate(`/clinic/${clinic.id}`)}
                                    >
                                        {t('clinicPage.directory.viewPage', { defaultValue: 'View Page' })}
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {!loading && pagination && pagination.current_page < pagination.last_page ? (
                <button type="button" className="clinic-directory-load-more" onClick={loadMore} disabled={loadingMore}>
                    {loadingMore
                        ? t("clinicPage.loadingMore", { defaultValue: "Loading..." })
                        : t("clinicPage.loadMore", { defaultValue: "Load more" })}
                </button>
            ) : null}
        </div>
    );
};

const ClinicListPage = () => {
    const {
        user,
        userType,
        getDisplayName,
        getProfileImageUrl,
        getUserType,
    } = useAuth();
    const { t } = useTranslation();

    // Format currentUser data like ChannelListPage does for SocialSidebar
    const currentUser = useMemo(() => {
        return user
            ? {
                id: user.id || user.user_id,
                name: getDisplayName(),
                username: user.username || `@${getDisplayName().replace(/\s+/g, "").toLowerCase()}`,
                profile_image_url: user.profile_image_url || user.profile_image || user.avatar || user.image || getProfileImageUrl() || getUserPlaceholderImage(user),
                avatar: user.profile_image_url || user.profile_image || user.avatar || user.image || getProfileImageUrl() || getUserPlaceholderImage(user),
                role: getUserType()?.replace(/\b\w/g, (l) => l.toUpperCase()) || "Pet Guardian",
                stats: {
                    posts: user.stats?.posts || user.posts_count || 0,
                    friends: user.stats?.friends ?? user.friends_count ?? 0,
                },
                friends_count: user.friends_count ?? user.stats?.friends ?? 0,
                average_rating: user.average_rating ?? 0,
                ratings_count: user.ratings_count ?? 0,
            }
            : {
                id: 0,
                name: t("common.loading", { defaultValue: "Loading..." }),
                username: "@loading",
                profile_image_url: getUserPlaceholderImage(null),
                avatar: getUserPlaceholderImage(null),
                role: "Pet Guardian",
                stats: { posts: 0, friends: 0 },
                friends_count: 0,
                average_rating: 0,
                ratings_count: 0,
            };
    }, [user, getDisplayName, getProfileImageUrl, getUserType, t]);

    return (
        <div className="social-page">
            <Navbar />
            <div className="social-page-container channel-page">
                <SocialSidebar currentUser={currentUser} userType={userType} />
                <div className="social-page-main">
                    <ClinicDirectory />
                </div>
            </div>
        </div>
    );
};

export default ClinicListPage;
